import { FC, useEffect } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { object, string } from 'yup';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, FormProvider } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form/dist/types';
import { api, clx } from 'Utils';
import { SubscriptionPlanDetails } from 'Types';
import {
	useAxios,
	useResponsive,
	useSubscriptionPlan,
	useSubscriptionPlanDetails,
	useSubscriptionPlansFeature
} from 'Hooks';
import {
	SubscriptionPlanDecorationBox,
	Card,
	LoadingContent,
	SubmitButton
} from 'Elements';
import styles from 'partials/page/form.module.scss';

export const UpdateSubscriptionPlans: FC = () => {
	const { t } = useTranslation();
	const { subscriptionPlan_id } = useParams();
	const { updateSubscriptionPlan } = useSubscriptionPlan(subscriptionPlan_id);
	const { sendRequest } = useAxios<string>();

	const { isDesktopAndBelow } = useResponsive();
	const {
		data: subscriptionPlan,
		isFetching,
		refetch
	} = useSubscriptionPlanDetails(subscriptionPlan_id as string);

	const {
		data: subscriptionPlansFeature,
		refetch: refetchSubscriptionPlansFeature
	} = useSubscriptionPlansFeature(
		subscriptionPlan?.subscriptionPlanFeatureId as string,
		subscriptionPlan?.id as string,
		{
			enabled: !!subscriptionPlan?.subscriptionPlanFeatureId
		}
	);

	const schema = object().shape({
		name: string().required(t('validation.title.required')),
		publishedInternships: string()
			.nullable()
			.required(t('validation.published-internships.required')),
		publishedJobs: string()
			.nullable()
			.required(t('validation.published-jobs.required')),
		termsCondition: string()
			.nullable()
			.required(t('validation.link-to-agb.required'))
			.matches(/^(https?:\/\/)/, t('validation.link-web-site'))
	});

	const formMethods = useForm<SubscriptionPlanDetails>({
		shouldUnregister: false,
		mode: 'onChange',
		resolver: yupResolver(schema),
		defaultValues: {}
	});

	const {
		reset,
		handleSubmit,
		setError,
		trigger,
		formState: { isDirty, isSubmitting, isValid }
	} = formMethods;

	const submitHandler: SubmitHandler<SubscriptionPlanDetails> = async (
		data
	) => {
		if (data?.topJobEnabled) {
			if (!data?.maxTopJobs) {
				setError('maxTopJobs', {
					type: 'required',
					message: t('validation.max-top-jobs.required')
				});
				return;
			}
			if (!data?.topJobExpiredAfterDays) {
				setError('topJobExpiredAfterDays', {
					type: 'required',
					message: t('validation.top-job-expired-after-days.required')
				});
				return;
			}
		}

		await sendRequest(
			api.updateSubscriptionPlanFeatures(
				subscriptionPlan_id as string,
				data.subscriptionPlanFeatureId
			),
			{
				method: 'put',
				data: {
					publishedJobs: data.publishedJobs,
					publishedInternships: data.publishedInternships,
					topJobEnabled: data.topJobEnabled,
					maxTopJobs: data.maxTopJobs,
					topJobExpiredAfterDays: data.topJobExpiredAfterDays
				}
			},

			async () => {
				let newData = data;

				if (data?.contactPerson?.fullName) {
					const contactPerson: any = {
						avatarUrl: data?.contactPerson?.avatarUrl,
						description: data?.contactPerson?.description,
						fullName: data?.contactPerson?.fullName,
						published: data?.contactPerson?.published ? true : false
					};
					newData = { ...data, contactPerson: contactPerson };
				} else {
					newData = { ...data, contactPerson: null as any };
				}
				await updateSubscriptionPlan(newData as any);
				refetchSubscriptionPlansFeature();
			}
		);
	};

	useEffect(() => {
		reset({
			...subscriptionPlan,
			publishedJobs:
				subscriptionPlansFeature?.jobSetting?.maxPublishJobs?.toString() ??
				'',
			availableUpgradePlanIds:
				subscriptionPlan?.availableUpgradePlanList ?? [],
			availableUpgradePlanList:
				subscriptionPlan?.availableUpgradePlanIds ?? [],
			isRecommended: subscriptionPlan?.isRecommended
				? subscriptionPlan?.isRecommended
				: false,
			publishedInternships:
				subscriptionPlansFeature?.internshipSetting?.maxPublishInternships?.toString() ??
				'',
			topJobEnabled: subscriptionPlansFeature?.jobSetting?.topJobEnabled,
			maxTopJobs:
				subscriptionPlansFeature?.jobSetting?.maxTopJobs?.toString() ??
				'',
			topJobExpiredAfterDays:
				subscriptionPlansFeature?.jobSetting?.topJobExpiredAfterDays?.toString() ??
				''
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subscriptionPlan, subscriptionPlansFeature]);

	// usePrompt(t('forms.leave-screen-massage'), isDirty);

	return (
		<FormProvider {...formMethods}>
			<form
				noValidate
				onSubmit={handleSubmit(submitHandler)}
				className={clx(
					isDesktopAndBelow && styles.form,
					'position-relative'
				)}>
				<SubscriptionPlanDecorationBox
					refetch={refetch}
					isValid={isValid}
					trigger={trigger}
					subscriptionPlan={subscriptionPlan as any}
				/>
				<Outlet context={subscriptionPlan} />
				{isFetching && <LoadingContent />}
				<div className="mt-3">
					<Card
						className={clx(
							isDesktopAndBelow && styles.sticky_action
						)}>
						<div className="d-flex align-items-center justify-content-end gap-2">
							<SubmitButton
								cancelButton="/subscription-plans"
								isDisable={!isDirty}
								isSubmitting={isSubmitting}
							/>
						</div>
					</Card>
				</div>
			</form>
		</FormProvider>
	);
};
